import React from 'react';
import { useTranslation } from 'react-i18next';

import { Button, TextField } from '@fly/core';
import {
  EXPIRATION_TIME_VALUES,
  GenerateTokenForm,
  generateTokenSchema,
} from '@fly/features/shared-features';
import { GeneratedToken, Permission, toastService } from '@fly/shared';
import { useFlyForm, useUser } from '@fly/shared-hooks';
import {
  openDialog,
  useAppDispatch,
  useGenerateTokenMutation,
} from '@fly/store';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import { Token } from '../Token/Token';

const extendedSchema = generateTokenSchema.extend({
  environmentName: z.string().min(1, 'Environment Name is required'),
});

type ExtendedFormData = z.infer<typeof extendedSchema>;

interface EnvironmentTokenDialogProps {
  onClose: () => void;
  environmentName: string;
}

export function EnvironmentTokenForm({
  onClose,
  environmentName,
}: EnvironmentTokenDialogProps) {
  const { t } = useTranslation();
  const [generateToken] = useGenerateTokenMutation();
  const { email } = useUser();
  const dispatch = useAppDispatch();
  const {
    control,
    handleSubmit,
    formState: { isValid, isSubmitting },
    reset,
  } = useFlyForm<ExtendedFormData>({
    resolver: zodResolver(extendedSchema),
    defaultValues: {
      tokenName: '',
      permissions: Permission.READ,
      expirationTime: EXPIRATION_TIME_VALUES['unlimited'].toString(),
      environmentName: environmentName,
    },
  });

  const handleFormSubmit = async (data: ExtendedFormData) => {
    const response = await generateToken({
      description: data.tokenName,
      scope: data.permissions,
      username: email!,
      expires_in: parseInt(data.expirationTime),
      environment: environmentName,
    });

    if (response.error) {
      return;
    }

    toastService.success(
      t('app.dashboard.token_management.messages.generate_token', {
        defaultValue: 'Token generated successfully',
      })
    );
    reset();
    tokenDialog(response.data!);
  };

  const tokenDialog = (token: GeneratedToken) => {
    const envName = environmentName;
    const title = t('app.dashboard.runtime_environments.token.title', {
      defaultValue: 'Environment Token Generated',
    });

    dispatch(
      openDialog({
        title: title,
        width: '5',
        content: <Token token={token} envName={envName} onClose={onClose} />,
        showCloseDialog: true,
      })
    );
  };
  return (
    <form
      onSubmit={handleSubmit(handleFormSubmit)}
      className='mt-fly-4 flex flex-col gap-fly-5'
    >
      <TextField
        label={t(
          'app.dashboard.runtime_environments.token_form.fields.environment_name.label',
          {
            defaultValue: 'Environment name',
          }
        )}
        value={environmentName}
        readOnly
        disabled
      />
      <GenerateTokenForm isPermissionsReadOnly control={control} />
      <div className='mt-fly-2 flex justify-end gap-fly-3'>
        <Button
          variant='ghost'
          type='button'
          onClick={onClose}
          disabled={isSubmitting}
        >
          {t('common.actions.cancel', { defaultValue: 'Cancel' })}
        </Button>
        <Button
          data-testid='runtime-environment-create-token-btn'
          type='submit'
          disabled={!isValid}
          loading={isSubmitting}
        >
          {t('common.actions.create', { defaultValue: 'Create' })}
        </Button>
      </div>
    </form>
  );
}
